import { useEffect, useState } from 'react';
import Stack from '@mui/joy/Stack';
import Card from '@mui/joy/Card';
import Button from '@mui/joy/Button';
import Table from '@mui/joy/Table';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';

import useSubscription from 'hooks/useSubscription';
import { PriceType } from "utils/types";
import { getIcon } from 'utils/misc';

import { Success } from './FancyToast';


declare global {
    namespace JSX {
        interface IntrinsicElements {
            'stripe-pricing-table': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
        }
    }
}

function Amount({ amount }: { amount: number }) {

    return (
        <Tooltip
            arrow
            title={`\$${Math.ceil(amount / 100)} per month`}>
            <Chip
                sx={{ cursor: 'default'}}
            variant="outlined"
                    color="success"
                >${Math.ceil(amount / 100)}
        </Chip>
            </Tooltip>

    );
}

function Plan({ plan }: { plan: PriceType }) {
    const { fetchPaymentLink } = useSubscription();
    const [loading, setLoading] = useState(false);
    function subscribe() {
        setLoading(true);
        Success("Loading secure checkout link");
        fetchPaymentLink(plan.id)
            .then((link) => {
                if (!link) return;
                Success("Redirecting to Stripe");
                setTimeout(() => {
                    window.location.href = link;
                }, 400);

                console.log("link", link);
            })
            .finally(() => setLoading(false));
    }
    return (
        <tr>
            <td align="left" width="40%">
                <Stack alignItems="flex-start">
                    <Typography
                        level="title-md">
                        {plan.product.name}
                    </Typography>
                    <Typography
                        level="body-sm">
                        {plan.product.description}
                    </Typography>
                </Stack>
            </td>
            <td align="center" width="10%">
                <Amount amount={plan.unit_amount} />
            </td>
            <td>
                <Stack
                    direction="row"
                    justifyContent="flex-end">
                    <Button
                        loading={loading}
                        onClick={subscribe}
                        variant="plain">Subscribe to {plan.product.name}</Button>
                </Stack>
            </td>
        </tr>
    );
}

function Help({ tag }: { tag: string }) {
    if (!tag) return;
    const WorldIcon = getIcon('Public');
    const MonthIcon = getIcon('CalendarMonth');
    const ReceiptLongIcon = getIcon('ReceiptLong');
    const TimeIcon = getIcon('Time');
    const PersonAddIcon = getIcon('PersonAdd');
    const ChatIcon = getIcon('Chat');
    const SunriseIcon = getIcon('Brightness7Outlined');
    const PersonPinIcon = getIcon('PersonPin');
    const EmojiPeopleIcon = getIcon('EmojiPeople');
    const PhonelinkIcon = getIcon('Phonelink');
    const GavelIcon = getIcon('Gavel');
    const SecurityIcon = getIcon('Security');
    const DefaultPrivacyIcon = getIcon('AdminPanelSettings');
    const ReplayIcon = getIcon('Replay');
    const PostOfficeIcon = getIcon('LocalPostOfficeOutlined');
    var notes = [];
    if (tag == 'plans-standard') {
        notes.push({
            icon: <WorldIcon color="primary" />,
            text: "The Standard Service is our world famous assistant service"
        });
        notes.push({
            icon: <MonthIcon />,
            text: "You get a specific number of requests to use each month"
        });
        notes.push({
            icon: <ReceiptLongIcon />,
            text: "Billed by the request"
        });
        // notes.push({
        //     icon: <SunriseIcon />,
        //     text: "Access to the Day Ones service (convert your requests into credits)"
        // });
        notes.push({
            icon: <ReplayIcon />,
            text: "Requests roll over every month"
        });
        notes.push({
            icon: <TimeIcon />,
            text: "A request is about 20 minutes of work by a human (give or take)"
        });
    }
    else if (tag == 'plans-day-ones') {
        notes.push(
            {
                icon: <SunriseIcon color="danger" />,
                text: "Day Ones is a complete rethinking of what a personal assistant service can be",
            });
        notes.push(
            {
                icon: <PersonAddIcon />,
                text: "Pick and choose from your favorite assistants and have them work together",
            });
        notes.push(
            {
                icon: <ChatIcon />,
                text: "Chat with your assistants in realtime",
            });
        notes.push({
            icon: <DefaultPrivacyIcon />,
            text: "Protect your privacy and control what your assistant can see"
        });
        notes.push(
            {
                icon: <PostOfficeIcon />,
                text: "A care package mailed to you (filled with Fancy Hands merch)",
            });
        notes.push(
            {
                icon: <TimeIcon />,
                text: "Billed by the hour/minute (does not roll over)",
            });
    }
    else if (tag == 'plans-dedicated') {
        notes.push({
            icon: <PersonPinIcon color="success" />,
            text: "Our Dedicated service pairs you with a single assistant"
        });
        notes.push({
            icon: <EmojiPeopleIcon />,
            text: "Work with the same assistant every time"
        });
        notes.push({
            icon: <PhonelinkIcon />,
            text: "Use our website or email, Slack, etc; However you work is fine with us"
        });
        notes.push({
            icon: <GavelIcon />,
            text: "No long term contract",
        });
        notes.push({
            icon: <SecurityIcon />,
            text: "Assistants with HIPAA certification available"
        });
        notes.push({
            icon: <TimeIcon />,
            text: "Billed by the hour/minute (does not roll over)",
        });
    }

    return (
        <Stack
            mb={2}
            spacing={0.5}
            alignItems="flex-start"
            direction="column">
            <Table>
                {notes.map((item, idx) => (
                    <tr id={idx.toString()}>
                        <td width="32">
                            {item.icon}
                        </td>
                        <td align="left">
                            <Typography
                                level={idx === 0 ? "body-sm" : "body-xs"}>
                                {item.text}</Typography>
                        </td>
                    </tr>
                ))}
            </Table>
        </Stack>
    );
}

function Plans({ title, plans, expanded, setExpanded, tag }: { title: string, plans: PriceType[], expanded: boolean, setExpanded: (expanded: boolean) => void, tag: string }) {
    if (!plans.length) return;
    const first = plans[0];
    const CloseIcon = getIcon('Close');
    return (
        <Card
            sx={{
                transition: 'all 0.3s ease-in-out',
                backgroundColor: (expanded ? undefined : 'transparent'),
                // maxHeight: removed ? 0 : '100vh',
            }}
            size={expanded ? 'md' : 'sm'}
            variant={expanded ? 'outlined' : 'plain'} >

            <Stack
                sx={{ cursor: 'pointer' }}
                onClick={() => setExpanded(!expanded)}
                direction="row"
                width="100%"
                justifyContent="space-between"

            >
                <Stack
                    spacing={1}
                    direction="row">

                    <Typography level="title-lg">
                        {title}
                    </Typography>

                    <Typography>
                        from
                    </Typography>
                    <Amount amount={first.unit_amount} />

                </Stack>
                {expanded &&
                    <CloseIcon />}
            </Stack>

            {expanded &&
                <Stack py={1}>
                    <Help tag={tag} />

                    <Table >
                        {plans.map((p) => (
                            <Plan key={p.id} plan={p} />
                        ))}
                    </Table>

                </Stack>
            }
        </Card>
    );
}

export default function PlansStripe() {
    const { fetchPriceList } = useSubscription();
    const [expanded, setExpanded] = useState('');
    const [standardPrices, setStandardPrices] = useState([] as PriceType[]);
    const [dedicatedPrices, setDedicatedPrices] = useState([] as PriceType[]);
    const [dayonesPrices, setDayonesPrices] = useState([] as PriceType[]);
    const DAY_ONES_PLANS = ['sunrise', 'morning', 'noon', 'golden-hour'];
    useEffect(() => {
        fetchPriceList()
            .then((a) => {
                if (!a) return;
                setStandardPrices(a.filter((p) => p.lookup_key.startsWith('standard')));
                setDedicatedPrices(a.filter((p) => p.lookup_key.startsWith('dedicated')));
                setDayonesPrices(a.filter((p) => DAY_ONES_PLANS.includes(p.lookup_key)));
            })
    }, [fetchPriceList]);

    function updateExpanded(s: string) {
        // setClicked(true);
        // setTimeout(() => setRemoved(true), 300);
        if (s == expanded)
            s = ''
        setExpanded(s);
    }

    return (
        <Stack sx={{ width: '100%' }} spacing={2}>

            <Stack
                sx={{
                    opacity: expanded ? 0.3 : 1,
                    transition: 'all 0.3s ease-in-out',
                    // maxHeight: removed ? 0 : '100vh',
                }}
                direction="row"
                spacing={1}
                alignItems="center">
                <Typography
                    pl={1}
                    level={expanded ? "body-xs" : "title-lg"}>
                    Pricing
                </Typography>

                {!expanded && <Typography
                    level="body-xs">
                    Click to find out more</Typography>}

            </Stack>

            <Plans
                title="The Standard Service"
                tag="plans-standard"
                plans={standardPrices}
                expanded={expanded == 'standard'}
                setExpanded={() => updateExpanded('standard')}
            />

            <Plans
                title="Day Ones"
                tag="plans-day-ones"
                plans={dayonesPrices}
                expanded={expanded == 'day-ones'}
                setExpanded={() => updateExpanded('day-ones')}
            />

            <Plans
                title="Dedicated Assistants"
                tag="plans-dedicated"
                plans={dedicatedPrices}
                expanded={expanded == 'dedicated'}
                setExpanded={() => updateExpanded('dedicated')}
            />

        </Stack>

    );
}
